/**
 * CalendarState.ts
 *
 * 캘린더 애플리케이션의 전역 상태를 관리하는 모듈
 *
 * 주요 기능:
 * - 전역 상태 변수 관리 (캘린더 목록, 이벤트 인스턴스, 알림 등)
 * - 이벤트 에미터를 통한 상태 변경 알림
 * - 상태 조회 및 업데이트 함수 제공
 * - 이벤트 리스너 등록/해제 관리
 * - 동기화 상태 관리
 *
 * 이 모듈은 다른 모든 캘린더 모듈들이 공유하는 중앙 집중식 상태 저장소 역할을 합니다.
 */

import { EventEmitter } from 'events'
import { reloadEvents } from './CalendarSync'
import settings from 'popups/Settings'
import { isSameDavUrl } from 'libs/dav/davPath'
import EventInstance from './EventInstance'

// CalendarClient에서 함수들 import
export { logout, initClient } from './CalendarClient'

/**
 * Calendar interface
 */
export interface Calendar {
  url: string
  isDefault?: boolean
  [key: string]: any
}

/**
 * Notification interface
 */
export interface Notification {
  id: string
  [key: string]: any
}

/**
 * User information interface
 */
export interface UserInfo {
  email?: string
  name?: string
  personaEmail?: string | null
  personaDomain?: string | null
  [key: string]: any
}

/**
 * Calendar settings interface
 */
interface CalendarSettings {
  defaultCalendar?: string
  lastAddedCalendar?: string
}

/**
 * Time range for event fetching
 */
interface TimeRange {
  start: string
  end: string
}

/**
 * Class info containing global state
 */
interface ClassInfo {
  calendars: Calendar[] | null
  hasSharedCalendars: boolean
  instances: EventInstance[]
  notifications: Notification[]
}

// 전역 상태 관리
export const classInfo: ClassInfo = {
  calendars: null,
  hasSharedCalendars: false,
  instances: [],
  notifications: [],
}

export const userInfo: UserInfo = {}
export let currentDate: Date = new Date()

// 이벤트 에미터
export const folderEmitter = new EventEmitter()
export const notificationEmitter = new EventEmitter()
export const instanceEmitter = new EventEmitter()
export const syncEmitter = new EventEmitter()

// 동기화 상태 변수들
export let isFetchingCalendars: boolean = false
export let fetchingEventQueue: any[] = []
export let latestTimeRange: Record<string, TimeRange> = {} //{ [folderUrl: string]: { start: '', end: '' } }
export let doingFetchNotifications: boolean = false

// 동기화 상태 수정 함수들
export const setIsFetchingCalendars = (value: boolean): void => {
  isFetchingCalendars = value
}

export const setFetchingEventQueue = (queue: any[]): void => {
  fetchingEventQueue = queue
}

export const setLatestTimeRange = (folderUrl: string, range: TimeRange): void => {
  latestTimeRange[folderUrl] = range
}

export const setDoingFetchNotifications = (value: boolean): void => {
  doingFetchNotifications = value
}

// 상태 업데이트 함수들
export const setCurrentDate = (date: Date): void => {
  if (currentDate === date) {
    return
  }
  let needReload = false
  if (
    currentDate.getFullYear() !== date.getFullYear() ||
    currentDate.getMonth() !== date.getMonth()
  ) {
    needReload = true
  }
  currentDate = date
  if (needReload) {
    reloadEvents()
  }
}

export const getCurrentDate = (): Date => {
  return currentDate
}

export const getUserInfo = (): UserInfo => {
  return userInfo
}

export const getCalendars = (): Calendar[] | null => {
  return classInfo.calendars
}

export const getDefaultCalendar = (): Calendar | null | undefined => {
  const calendar = settings.getValue('calendar') as CalendarSettings
  const defaultCalendar = calendar?.defaultCalendar
  const lastAddedCalendar = calendar?.lastAddedCalendar

  let folder: Calendar | undefined = undefined
  if (defaultCalendar) {
    folder = classInfo.calendars?.find((c) => isSameDavUrl(c.url, defaultCalendar))
    if (folder) {
      return folder
    }
  }

  if (lastAddedCalendar) {
    folder = classInfo.calendars?.find((c) => isSameDavUrl(c.url, lastAddedCalendar))
    if (folder) {
      return folder
    }
  }

  return classInfo.calendars?.find((c) => c.isDefault)
}

export const hasSharedCalendars = (): boolean => {
  return classInfo.hasSharedCalendars
}

export const getInstances = (): EventInstance[] => {
  return classInfo.instances
}

export const getInstance = (instanceId: string): EventInstance | null => {
  return classInfo.instances.find((i) => i.id === instanceId) || null
}

export const getNotifications = (): Notification[] => {
  return classInfo.notifications
}

// 이벤트 리스너 관리
export const addNotificationListener = (listener: () => void): void => {
  notificationEmitter.on('notification', listener)
}

export const removeNotificationListener = (listener: () => void): void => {
  notificationEmitter.off('notification', listener)
}

export const addFolderListener = (listener: () => void): void => {
  folderEmitter.on('folder', listener)
}

export const removeFolderListener = (listener: () => void): void => {
  folderEmitter.off('folder', listener)
}

export const addInstanceListener = (listener: () => void): void => {
  instanceEmitter.on('instance', listener)
}

export const removeInstanceListener = (listener: () => void): void => {
  instanceEmitter.off('instance', listener)
}

export const addSyncListener = (listener: (isSyncing: boolean) => void): void => {
  syncEmitter.on('sync', listener)
}

export const removeSyncListener = (listener: (isSyncing: boolean) => void): void => {
  syncEmitter.off('sync', listener)
}

export const _emitSync = (): void => {
  syncEmitter.emit('sync', isFetchingCalendars || fetchingEventQueue.length > 0)
}

export const isSyncing = (): boolean => {
  return isFetchingCalendars || fetchingEventQueue.length > 0
}
